/**
 * Regras de leitura da faixa de indicadores da composição (a receber × a pagar).
 *
 * PEDIDO DO USUÁRIO (09/10/2026): "tanto o valor total em aberto (do que
 * conseguimos conciliar), quanto os informativos de valores que conseguimos
 * conciliar, o valor que não conseguimos e o valor total da empresa... tanto
 * pra crédito quanto pra débito".
 *
 * TUDO AQUI É FUNÇÃO PURA, para que a regra seja testável sem renderizar. O
 * componente só escolhe cor e posição.
 */

import { ILadoComposicao } from './conta/papelTrabalho';

/**
 * Por quanto tempo a composição já buscada é considerada FRESCA.
 *
 * A composição é uma FOTOGRAFIA: o job em lote a grava uma vez por dia, às
 * 03:00. Reconsultar a cada 5 minutos pedia de novo um número que não muda
 * até a madrugada seguinte. 30 minutos ainda pega o recálculo manual de uma
 * empresa sem cobrar do banco a cada volta à tela.
 */
export const COMPOSICAO_FRESCA_MS = 30 * 60 * 1000;

/**
 * Por quanto tempo a empresa que SAIU da tela continua guardada.
 *
 * O padrão do React Query é 5 min, e foi o que fez a Conferência de
 * Importações descartar o mês anterior: voltar a ele recomeçava do zero, com
 * a tela vazia. Aqui o caminho é o mesmo — o contador compara empresa A com
 * empresa B e volta para a A.
 *
 * Guardado por 2 h, voltar à empresa mostra a faixa no mesmo instante. Como
 * `staleTime` é menor, o número ainda é conferido em segundo plano quando já
 * passou da validade — sem nunca mostrar tela vazia.
 */
export const COMPOSICAO_CACHE_MS = 2 * 60 * 60 * 1000;

/** O rótulo de cada lado. Dado, não `if` espalhado pelo JSX. */
export const ROTULO_LADO: Record<ILadoComposicao['lado'], string> = {
  receber: 'A receber',
  pagar: 'A pagar',
  outro: 'Outras contas',
};

/**
 * O que cada lado É, em uma linha — o contador precisa saber de onde vem a
 * separação antes de confiar nela.
 */
export const EXPLICA_LADO: Record<ILadoComposicao['lado'], string> = {
  receber: 'Contas de ativo (classificação 1): clientes, adiantamentos a receber.',
  pagar: 'Contas de passivo (classificação 2): fornecedores, obrigações a pagar.',
  outro: 'Contas cuja classificação não começa com 1 nem 2.',
};

/**
 * `true` quando o lado tem algo a mostrar.
 *
 * Um lado inteiramente zerado não é informação: é ausência. Mostrá-lo como
 * "R$ 0,00" faria o contador procurar um movimento que não existe. A régua
 * inclui `contas` porque uma conta sem item ainda é uma conta da empresa.
 */
export const ladoTemDado = (l: ILadoComposicao | undefined): boolean => {
  if (!l) return false;
  return (
    Number(l.contas ?? 0) > 0 ||
    Number(l.provisionado ?? 0) !== 0 ||
    Number(l.sem_par ?? 0) !== 0 ||
    Number(l.saldo_razao ?? 0) !== 0
  );
};

/** Os lados que a tela deve desenhar, na ordem, já sem os vazios. */
export const ladosVisiveis = (lados: ILadoComposicao[] | undefined): ILadoComposicao[] =>
  (lados ?? []).filter(ladoTemDado);

/**
 * `true` quando vale mostrar a quebra por lado.
 *
 * Com um lado só, as duas colunas repetiriam o total — duas réguas para o mesmo
 * número, que é a família de defeitos "card e drawer contam coisas diferentes".
 * Nesse caso a tela mostra só o total.
 */
export const valeQuebrarPorLado = (lados: ILadoComposicao[] | undefined): boolean =>
  ladosVisiveis(lados).length > 1;

/**
 * Quanto do provisionado já foi conciliado, em %.
 *
 * `null` quando não há provisionado: 0% afirmaria "nada foi conciliado" onde o
 * correto é "não há título a conciliar". A mesma regra do `pct_casado`.
 */
export const pctConciliado = (conciliado: number, provisionado: number): number | null => {
  if (!Number.isFinite(provisionado) || provisionado <= 0) return null;
  return Math.round((conciliado / provisionado) * 1000) / 10;
};

/**
 * O texto de cobertura dos títulos ligados.
 *
 * Mostra a RAZÃO, não só o total: "815 de 26.904" diz muito mais que "815",
 * porque 815 de 1.000 e 815 de 26.904 são situações opostas.
 */
export const textoCobertura = (l: {
  titulos_ligados?: number | null;
  titulos?: number | null;
}): string => {
  const tot = Number(l.titulos ?? 0);
  if (!tot) return '—';
  const lig = Number(l.titulos_ligados ?? 0);
  return `${lig.toLocaleString('pt-BR')} de ${tot.toLocaleString('pt-BR')}`;
};

/**
 * `true` quando a soma dos lados NÃO fecha com o total declarado.
 *
 * O backend calcula o total somando os lados, então isto deve ser sempre
 * `false`. Existe como detector: se um dia a regra divergir, a tela avisa em
 * vez de mostrar dois números que se contradizem em silêncio — foi exatamente
 * assim que "100% de sucesso com 147 falhas" chegou à produção.
 *
 * Tolerância de 1 centavo: somar `numeric(18,2)` em ponto flutuante acumula
 * resíduo (medido em outra tela: 7,45e-9 em 79 contas).
 */
export const somaNaoFecha = (
  lados: ILadoComposicao[] | undefined,
  total: { provisionado?: number } | undefined,
): boolean => {
  if (!lados?.length || !total) return false;
  const soma = lados.reduce((s, l) => s + Number(l.provisionado ?? 0), 0);
  return Math.abs(soma - Number(total.provisionado ?? 0)) > 0.01;
};

/**
 * As fatias da barra de proporção: conciliado, em aberto e sem par.
 *
 * A barra existe porque três valores absolutos lado a lado não dizem a
 * PROPORÇÃO entre eles — e é a proporção que responde "como está a
 * conciliação desta empresa". Pesquisa de dashboard converge nisso: a memória
 * de trabalho segura ~4 blocos, então o que puder virar forma em vez de
 * número, vira.
 *
 * Devolve percentuais que somam 100. `null` quando não há base: uma barra
 * vazia afirmaria "nada conciliado" onde o certo é "não há o que medir".
 */
export const fatiasDaBarra = (l: {
  conciliado?: number | null;
  em_aberto?: number | null;
  sem_par_real?: number | null;
}): { conciliado: number; aberto: number; semPar: number } | null => {
  const c = Math.max(0, Number(l.conciliado ?? 0));
  const a = Math.max(0, Number(l.em_aberto ?? 0));
  const s = Math.max(0, Number(l.sem_par_real ?? 0));
  const tot = c + a + s;
  if (!Number.isFinite(tot) || tot <= 0) return null;
  // Arredonda as duas primeiras e dá o RESTO à última. Arredondar as três
  // separadamente deixa um vão: com 1/3 cada, 33,3 x 3 = 99,9 e sobra 0,1% de
  // trilho vazio no fim da barra.
  const pc = Math.round((c / tot) * 1000) / 10;
  const pa = Math.round((a / tot) * 1000) / 10;
  return {
    conciliado: pc,
    aberto: pa,
    semPar: Math.round((100 - pc - pa) * 10) / 10,
  };
};

/**
 * O rótulo do que a barra mostra, para leitor de tela e para o `title`.
 *
 * Barra sem legenda textual é decoração: quem usa teclado ou leitor de tela
 * não enxerga proporção nenhuma.
 */
export const descreveBarra = (l: {
  conciliado?: number | null;
  em_aberto?: number | null;
  sem_par_real?: number | null;
}): string => {
  const f = fatiasDaBarra(l);
  if (!f) return 'Sem movimento para medir.';
  return (
    `Conciliado ${f.conciliado.toFixed(1)}%, ` +
    `em aberto ${f.aberto.toFixed(1)}%, ` +
    `sem par ${f.semPar.toFixed(1)}%.`
  );
};

/**
 * A chave da query da faixa.
 *
 * MORA AQUI, e não inline no JSX, por causa de um defeito real: a chave tinha
 * só a empresa, então trocar o ano do filtro não refazia a consulta e a faixa
 * repetia os mesmos valores em qualquer exercício ("mesmo que seja para ano
 * anterior, ele ainda continua apresentando os mesmos valores", 14/10/2026).
 *
 * Inline, nenhum teste alcançava a chave: desfazer a correção mantinha as 496
 * suítes verdes. Como função pura, a regra fica travada — o `exercicio`
 * PRECISA estar na chave, senão o React Query serve o cache do ano anterior.
 */
export const chaveComposicaoLados = (
  empresaId: number | undefined,
  exercicio: number,
): [string, { id: number | undefined; exercicio: number }] => [
  'composicao-lados',
  { id: empresaId, exercicio },
];
